"use client";

/**
 * `<BriefMini>` — small archived-brief card. Hover lifts + brightens
 * the box-shadow. Click navigates to the public sample-briefs index.
 */

import { memo, useState } from "react";
import Link from "next/link";
import { ArrowR } from "../icons";

interface BriefMiniProps {
  sym: string;
  date: string;
  verdict: string;
  score: string;
  color: string;
  synth: string;
  /** Per-brief share URL. Defaults to the auth-gated dashboard list
   *  when omitted (preserves the legacy placeholder behaviour). */
  href?: string;
}

function BriefMiniImpl({
  sym,
  date,
  verdict,
  score,
  color,
  synth,
  href = "/compass/briefs",
}: BriefMiniProps) {
  const [hover, setHover] = useState(false);

  return (
    <Link
      href={href}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      style={{
        display: "block",
        background: "var(--color-surface)",
        border: "1px solid var(--color-line)",
        borderRadius: 14,
        padding: 28,
        cursor: "pointer",
        transition: "all 0.25s",
        transform: hover ? "translateY(-3px)" : "none",
        boxShadow: hover
          ? "0 16px 40px -16px rgba(0,0,0,0.5)"
          : "none",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 20,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 22,
              fontWeight: 600,
              color: "var(--color-text)",
              letterSpacing: "-0.015em",
            }}
          >
            {sym}
          </div>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              color: "var(--color-dim)",
              letterSpacing: "0.14em",
              marginTop: 4,
              textTransform: "uppercase",
            }}
          >
            {date}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div
            style={{
              fontSize: 28,
              fontWeight: 600,
              color,
              letterSpacing: "-0.02em",
              lineHeight: 1,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {score}
          </div>
          <div
            style={{
              fontSize: 10,
              color: "var(--color-dim)",
              fontFamily: "var(--font-mono)",
              letterSpacing: "0.1em",
              marginTop: 2,
              textTransform: "uppercase",
            }}
          >
            /10 · {verdict}
          </div>
        </div>
      </div>
      <div
        style={{
          height: 1,
          background: "var(--color-line-2)",
          marginBottom: 16,
        }}
      />
      <p
        style={{
          fontSize: 14,
          color: "var(--color-muted)",
          lineHeight: 1.6,
          margin: 0,
          marginBottom: 20,
        }}
      >
        {synth}
      </p>
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          fontSize: 13,
          color: "var(--color-blue-2)",
          fontWeight: 500,
        }}
      >
        Read full brief{" "}
        <span
          style={{
            display: "inline-flex",
            transform: hover ? "translateX(4px)" : "none",
            transition: "transform 0.2s",
          }}
        >
          <ArrowR />
        </span>
      </div>
    </Link>
  );
}

export const BriefMini = memo(BriefMiniImpl);
